import { useCallback, useState, useEffect } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import type { SourceSystem, MappingConfig, UploadedFile } from '@types';
import { mappingApi } from '@services/api';

interface FileUploaderProps {
  onFilesSelected: (
    files: File[],
    sourceSystem: SourceSystem,
    mappingConfigId?: string
  ) => void;
  isProcessing?: boolean;
  maxFiles?: number;
}

const ACCEPTED_FILE_TYPES = {
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
  'application/vnd.ms-excel': ['.xls'],
  'application/json': ['.json'],
  'application/xml': ['.xml'],
  'text/xml': ['.xml'],
  'text/plain': ['.txt'],
};

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const SOURCE_SYSTEMS: { label: string; value: SourceSystem }[] = [
  { label: 'QuickBooks', value: 'QUICKBOOKS' },
  { label: 'ADP', value: 'ADP' },
  { label: 'Paychex', value: 'PAYCHEX' },
  { label: 'Gusto', value: 'GUSTO' },
  { label: 'Workday', value: 'WORKDAY' },
];

export default function FileUploader({
  onFilesSelected,
  isProcessing = false,
  maxFiles = 10,
}: FileUploaderProps) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [sourceSystem, setSourceSystem] = useState<SourceSystem>('QUICKBOOKS');
  const [mappingConfigs, setMappingConfigs] = useState<MappingConfig[]>([]);
  const [selectedMappingId, setSelectedMappingId] = useState<string>('');
  const [loadingMappings, setLoadingMappings] = useState(false);

  // Load mapping configurations when source system changes
  useEffect(() => {
    const loadMappings = async () => {
      setLoadingMappings(true);
      try {
        const result = await mappingApi.getMappingsBySystem(sourceSystem);
        // Handle both array and object with mappings property
        const mappings = Array.isArray(result)
          ? result
          : ((result as { mappings?: MappingConfig[] })?.mappings ?? []);
        setMappingConfigs(mappings);

        // Auto-select default mapping if exists
        const defaultMapping = mappings.find(m => m.is_default);
        if (defaultMapping) {
          setSelectedMappingId(defaultMapping.id);
        } else {
          setSelectedMappingId('');
        }
      } catch (error) {
        console.error('Failed to load mappings:', error);
        setMappingConfigs([]);
        setSelectedMappingId('');
      } finally {
        setLoadingMappings(false);
      }
    };

    loadMappings();
  }, [sourceSystem]);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      const newFiles: UploadedFile[] = acceptedFiles.map(file => ({
        file,
        preview: undefined,
      }));

      const rejectedUploads: UploadedFile[] = rejectedFiles.map(rejected => ({
        file: rejected.file,
        error: rejected.errors[0]?.message || 'File rejected',
      }));

      setUploadedFiles(prev => [...prev, ...newFiles, ...rejectedUploads]);
    },
    []
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      accept: ACCEPTED_FILE_TYPES,
      maxSize: MAX_FILE_SIZE,
      maxFiles,
      disabled: isProcessing,
    });

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setUploadedFiles([]);
  };

  const handleUpload = () => {
    const validFiles = uploadedFiles.filter(f => !f.error).map(f => f.file);
    if (validFiles.length > 0) {
      onFilesSelected(validFiles, sourceSystem, selectedMappingId || undefined);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (_file: File) => {
    return <DocumentIcon className="h-8 w-8 text-blue-500" />;
  };

  const validFilesCount = uploadedFiles.filter(f => !f.error).length;
  const hasErrors = uploadedFiles.some(f => f.error);

  return (
    <div className="space-y-6">
      {/* Source System Selector */}
      <div>
        <label
          htmlFor="source-system"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Source System
        </label>
        <select
          id="source-system"
          value={sourceSystem}
          onChange={e => setSourceSystem(e.target.value as SourceSystem)}
          disabled={isProcessing}
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          {SOURCE_SYSTEMS.map(system => (
            <option key={system.value} value={system.value}>
              {system.label}
            </option>
          ))}
        </select>
      </div>

      {/* Mapping Configuration Selector */}
      <div>
        <label
          htmlFor="mapping-config"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Mapping Configuration (Optional)
        </label>
        <select
          id="mapping-config"
          value={selectedMappingId}
          onChange={e => setSelectedMappingId(e.target.value)}
          disabled={isProcessing || loadingMappings}
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">Use Default Mapping</option>
          {mappingConfigs.map(config => (
            <option key={config.id} value={config.id}>
              {config.name} {config.is_default ? '(Default)' : ''}
            </option>
          ))}
        </select>
        {loadingMappings && (
          <p className="mt-1 text-xs text-gray-500">Loading mappings...</p>
        )}
      </div>

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive && !isDragReject ? 'border-blue-500 bg-blue-50' : ''}
          ${isDragReject ? 'border-red-500 bg-red-50' : ''}
          ${!isDragActive && !isDragReject ? 'border-gray-300 hover:border-gray-400' : ''}
          ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input {...getInputProps()} />
        <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-2 text-sm text-gray-600">
          {isDragActive ? (
            isDragReject ? (
              <span className="text-red-600">Some files will be rejected</span>
            ) : (
              <span className="text-blue-600">Drop files here...</span>
            )
          ) : (
            <>
              <span className="font-semibold">Click to upload</span> or drag and
              drop
            </>
          )}
        </p>
        <p className="mt-1 text-xs text-gray-500">
          CSV, XLSX, XLS, JSON, XML, or TXT up to 50MB (max {maxFiles} files)
        </p>
      </div>

      {/* File List */}
      <AnimatePresence>
        {uploadedFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-3"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">
                Selected Files ({validFilesCount})
              </h3>
              <button
                type="button"
                onClick={clearAll}
                disabled={isProcessing}
                className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear All
              </button>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {uploadedFiles.map((uploadedFile, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className={`
                    flex items-center justify-between p-3 rounded-lg border
                    ${uploadedFile.error ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}
                  `}
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {getFileIcon(uploadedFile.file)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {uploadedFile.file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(uploadedFile.file.size)}
                      </p>
                      {uploadedFile.error && (
                        <p className="text-xs text-red-600 mt-1">
                          {uploadedFile.error}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {uploadedFile.error ? (
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                    ) : (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    )}
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      disabled={isProcessing}
                      className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-400" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            {hasErrors && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  Some files cannot be uploaded. Please remove them or fix the
                  issues.
                </p>
              </div>
            )}

            {/* Upload Button */}
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={handleUpload}
                disabled={isProcessing || validFilesCount === 0 || hasErrors}
                className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing
                  ? 'Processing...'
                  : `Upload ${validFilesCount} File${validFilesCount !== 1 ? 's' : ''}`}
              </button>
              <button
                type="button"
                onClick={clearAll}
                disabled={isProcessing}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
